import { useCallback, useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { BaseDialog } from '../../common/dialogs/BaseDialog';
import { PromptsLibraryLeftPanel } from './left-panel/PromptsLibraryLeftPanel';
import { AllPromptsView } from './right-panel/AllPromptsView';
import { CategoryView } from './right-panel/CategoryView';
import { usePromptGroupState } from './usePromptGroupState';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { useSelector } from 'react-redux';
import {
  selectIsLoading,
  selectIsLoadingPrompts,
  selectPrompts,
} from '../../../reducers/promptLibraryReducer';
import useInitializePromptGroups from './useInitializePromptGroups';
import { useCategoryActions } from './useCategoryActions';
import {
  CategoryData,
  CategoryFormState,
  PromptViewMode,
  PromptViewState,
  PromptWithCategory,
} from './types';
import { PromptEditScreen } from './PromptEditScreen';
import { usePrompt } from './usePrompt';
import { ArtifactProvider } from '../../common/artifacts/useArtifacts';

interface PromptsLibraryV2DialogProps {
  open: boolean;
  onClose: () => void;
}

export const PromptsLibraryV2Dialog = ({ open, onClose }: PromptsLibraryV2DialogProps) => {
  useInitializePromptGroups();
  const isLoading = useSelector(selectIsLoading);
  const isLoadingPrompts = useSelector(selectIsLoadingPrompts);
  const promptsFromStore = useSelector(selectPrompts);
  const [categoryFormState, setCategoryFormState] = useState<CategoryFormState>({
    mode: null,
    editingCategoryId: null,
  });
  const [promptViewState, setPromptViewState] = useState<PromptViewState>({
    mode: PromptViewMode.list,
  });
  const isFormOpen = categoryFormState.mode !== null;

  const {
    categories,
    selectedCategoryId,
    filteredPrompts,
    totalPromptsCount,
    handleCategorySelect,
    clearSelectedCategoryIfDeleted,
  } = usePromptGroupState();

  const { loadPrompts, createPrompt, updatePrompt, removePrompt } = usePrompt();

  useEffect(() => {
    if (open && !promptsFromStore) {
      loadPrompts();
    }
  }, [open, promptsFromStore, loadPrompts]);

  useEffect(() => {
    if (!open) {
      handleCategorySelect(null);
      setPromptViewState({ mode: PromptViewMode.list });
    }

    return () => {
      handleCategorySelect(null);
    };
  }, [open, handleCategorySelect]);

  const {
    handleCreateCategory: createCategory,
    handleEditCategory: editCategory,
    handleDeleteCategory: deleteCategory,
    handlePinCategory: pinCategory,
    handleUnpinCategory: unpinCategory,
  } = useCategoryActions(categories);

  const handleCreateCategory = useCallback(
    async (data: CategoryData) => {
      const newGroup = await createCategory(data, 'UserPlus');
      if (newGroup) {
        handleCategorySelect(newGroup.id);
      }
    },
    [createCategory, handleCategorySelect],
  );

  const handleEditCategory = useCallback(
    async (categoryId: string, data: CategoryData) => {
      const category = categories.find((c) => c.id === categoryId);
      if (!category) return;
      await editCategory(categoryId, data, category.iconName ?? 'UserPlus');
    },
    [editCategory, categories],
  );

  const handleDeleteCategory = useCallback(
    async (categoryId: string) => {
      await deleteCategory(categoryId);
      clearSelectedCategoryIfDeleted(categoryId);
    },
    [deleteCategory, clearSelectedCategoryIfDeleted],
  );

  const handlePinCategory = useCallback(
    async (categoryId: string) => {
      await pinCategory(categoryId);
    },
    [pinCategory],
  );

  const handleUnpinCategory = useCallback(
    async (categoryId: string) => {
      await unpinCategory(categoryId);
    },
    [unpinCategory],
  );

  const handlePromptRowClick = (prompt: PromptWithCategory) => {
    setPromptViewState({ mode: PromptViewMode.edit, prompt });
  };

  const handleCreatePrompt = useCallback(() => {
    setPromptViewState({ mode: PromptViewMode.create, initialCategoryId: selectedCategoryId });
  }, [selectedCategoryId]);

  const handleBackToList = useCallback(() => {
    setPromptViewState({ mode: PromptViewMode.list });
  }, []);

  const handleSavePrompt = useCallback(
    async (
      promptData: { name: string; promptText: string; promptGroupId?: string },
      promptId?: string,
    ) => {
      if (promptId) {
        await updatePrompt(promptId, {
          name: promptData.name,
          promptText: promptData.promptText,
          promptGroupId: promptData.promptGroupId,
        });
      } else {
        await createPrompt(promptData);
      }
    },
    [createPrompt, updatePrompt],
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId],
  );

  const renderContent = () => {
    if (isLoading || isLoadingPrompts) {
      return <CenteredCircularProgress />;
    }

    if (promptViewState.mode === PromptViewMode.create) {
      return (
        <PromptEditScreen
          categories={categories}
          initialCategoryId={promptViewState.initialCategoryId}
          promptToEdit={null}
          onClose={handleBackToList}
          onSave={handleSavePrompt}
        />
      );
    }

    if (promptViewState.mode === PromptViewMode.edit) {
      return (
        <PromptEditScreen
          categories={categories}
          initialCategoryId={promptViewState.prompt.categoryId}
          promptToEdit={promptViewState.prompt}
          onClose={handleBackToList}
          onSave={handleSavePrompt}
        />
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <PromptsLibraryLeftPanel
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={handleCategorySelect}
          onCreateCategory={handleCreateCategory}
          onEditCategory={handleEditCategory}
          onDeleteCategory={handleDeleteCategory}
          onPinCategory={handlePinCategory}
          onUnpinCategory={handleUnpinCategory}
          totalPromptsCount={totalPromptsCount}
          categoryFormState={categoryFormState}
          onCategoryFormStateChange={setCategoryFormState}
        />
        <Box
          sx={{
            pointerEvents: isFormOpen ? 'none' : 'auto',
            opacity: isFormOpen ? 0.5 : 1,
            transition: 'opacity 0.2s',
            flex: 1,
            minHeight: 0,
          }}
        >
          {selectedCategory ? (
            <CategoryView
              key={selectedCategory.id}
              prompts={filteredPrompts}
              category={selectedCategory}
              onClose={onClose}
              onCreatePrompt={handleCreatePrompt}
              onPromptClick={handlePromptRowClick}
              onDeletePrompt={removePrompt}
            />
          ) : (
            <AllPromptsView
              prompts={filteredPrompts}
              categories={categories}
              onClose={onClose}
              onCreatePrompt={handleCreatePrompt}
              onDeletePrompt={removePrompt}
              onPromptClick={handlePromptRowClick}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <ArtifactProvider>
      <BaseDialog
        open={open}
        disableBackdropClickClose={
          isFormOpen || [PromptViewMode.edit, PromptViewMode.create].includes(promptViewState.mode)
        }
        onClose={onClose}
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: 'calc(100% - 164px)',
            height: '100%',
            width: '100%',
            maxWidth: 'calc(100% - 164px)',
            overflow: 'hidden',
          },
        }}
      >
        {renderContent()}
      </BaseDialog>
    </ArtifactProvider>
  );
};
